import { useMemo } from 'react'
import { useI18n } from '../../i18n'
import type { RiskComplianceExecutiveSummary } from '../../services/enterpriseOperationalUxApi'
import { getStandardDisplayName } from '../../utils/complianceStandard'
import styles from '../../styles/components/assets/AssetsRiskCompliance.module.scss'

type Props = {
  standardRows: RiskComplianceExecutiveSummary['standardRows']
  blastImpacts: RiskComplianceExecutiveSummary['blastRadiusImpacts']
}

const NODE_W = 108
const NODE_H = 40

export default function RiskComplianceRiskMiniGraph({ standardRows, blastImpacts }: Props) {
  const { t } = useI18n()

  const { nodes, edges, width, height } = useMemo(() => {
    const degraded = standardRows.filter((r) => r.isDegradedStandard).slice(0, 8)
    const positioned = degraded.map((n, i) => ({
      id: n.standardCode,
      label: n.standardCode,
      pressure: n.pressure,
      risk: n.certificationRiskLevel,
      x: 24 + (i % 4) * (NODE_W + 20),
      y: 24 + Math.floor(i / 4) * (NODE_H + 36),
    }))
    const edgeList: Array<{ id: string; from: string; to: string; propagates: boolean }> = []
    for (const imp of blastImpacts.slice(0, 6)) {
      const standards = imp.impactedStandards || []
      for (let i = 0; i < standards.length - 1; i++) {
        const from = standards[i]
        const to = standards[i + 1]
        if (positioned.some((n) => n.id === from) && positioned.some((n) => n.id === to)) {
          edgeList.push({ id: `${imp.controlCode}-${from}-${to}`, from, to, propagates: true })
        }
      }
    }
    const cols = Math.min(4, Math.max(1, positioned.length))
    const rows = Math.ceil(positioned.length / cols) || 1
    return {
      nodes: positioned,
      edges: edgeList,
      width: Math.max(320, cols * (NODE_W + 20) + 48),
      height: Math.max(140, rows * (NODE_H + 36) + 48),
    }
  }, [standardRows, blastImpacts])

  if (!nodes.length) {
    return (
      <p className={styles.widgetEmpty}>
        {t('enterpriseOperationalUx.riskCompliance.noCertificationInstability', 'No certification instability')}
      </p>
    )
  }

  return (
    <div className={styles.rcGraphWrap}>
      <p className={styles.rcGraphLegend}>
        {t('enterpriseOperationalUx.riskCompliance.graphLegend', 'Nodes: certification risk · pressure. Edges: propagates degradation.')}
      </p>
      <svg className={styles.rcGraphSvg} viewBox={`0 0 ${width} ${height}`} role="img">
        {edges.map((e) => {
          const s = nodes.find((n) => n.id === e.from)
          const tg = nodes.find((n) => n.id === e.to)
          if (!s || !tg) return null
          return (
            <line
              key={e.id}
              x1={s.x + NODE_W / 2}
              y1={s.y + NODE_H}
              x2={tg.x + NODE_W / 2}
              y2={tg.y}
              className={`${styles.rcGraphEdge} ${e.propagates ? styles.rcGraphEdgePropagates : ''}`}
            />
          )
        })}
        {nodes.map((n) => {
          const cls =
            n.risk === 'CRITICAL' || n.risk === 'AT_RISK'
              ? styles.rcGraphNodeRisk
              : n.pressure === 'HIGH' || n.pressure === 'CRITICAL'
                ? styles.rcGraphNodePressure
                : styles.rcGraphNode
          return (
            <g key={n.id} transform={`translate(${n.x},${n.y})`}>
              <rect width={NODE_W} height={NODE_H} rx={6} className={cls} />
              <text x={NODE_W / 2} y={NODE_H / 2 + 4} textAnchor="middle" className={styles.rcGraphLabel}>
                {getStandardDisplayName(t, n.label).slice(0, 12)}
              </text>
            </g>
          )
        })}
      </svg>
    </div>
  )
}
